import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../context/NotificationContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShare, 
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@mantine/core';
import Navigation from '../Home/Navigation';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import ConnectedAccountsSection from './ConnectedAccountsSection';
import ProjectGridSection from './ProjectGridSection';
import SocialMediaShareModal from './SocialMediaShareModal';
import { socialMediaService } from '../../services/socialMediaService';
import { fetchProjects } from '../../services/projectService';
import { 
  SocialAccount, 
  ProjectWithVideos, 
  SocialPlatform,
  SocialPost
} from './types';
import styles from './SocialMediaLanding.module.css';

const SocialMediaLanding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const { t } = useTranslation('socialMediaLanding');
  
  // State management
  const [projects, setProjects] = useState<ProjectWithVideos[]>([]);
  const [publishedPosts, setPublishedPosts] = useState<SocialPost[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectWithVideos | null>(null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);

  // Load projects with videos
  const loadProjects = async () => {
    try {
      setIsProjectsLoading(true);
      const response = await fetchProjects(1, 1000); // Get all projects
      const allProjects = response.projects || [];
      
      // Filter to only include projects with videos
      const videoProjects = allProjects.filter(project => 
        project.originalVideoUrl || 
        project.captionedVideoUrl || 
        project.generatedVideoUrl
      );
      
      setProjects(videoProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects');
    } finally {
      setIsProjectsLoading(false);
    }
  };
  
  // Load published posts
  const loadPublishedPosts = async () => {
    try {
      const posts = await socialMediaService.getUserPosts();
      setPublishedPosts(posts);
    } catch (error) {
      console.error('Error loading published posts:', error);
      // Don't show error to user - published posts are optional
    }
  };

  // Initial load
  useEffect(() => {
    loadProjects();
    loadPublishedPosts();
  }, []);

  // Handle project-id URL parameter to auto-open modal
  useEffect(() => {
    const projectId = searchParams.get('project-id');
    
    if (projectId && projects.length > 0 && !shareModalOpen) {
      // Find the project by ID
      const project = projects.find(p => p.id === projectId);
      
      if (project) {
        // Determine which video URL to use (prioritize in this order)
        // Check both direct property and metadata path for soundtrack
        const soundtrackUrl = project.soundTrackVideoUrl || project.metadata?.soundtrack?.soundtrackVideoUrl;
        
        const videoUrl = 
          soundtrackUrl || 
          project.captionedVideoUrl || 
          project.generatedVideoUrl || 
          project.originalVideoUrl;
        
        // Open the share modal
        handleShare(project, videoUrl);
        
        // Optional: Remove the project-id param from URL after opening
        // This prevents the modal from reopening on refresh
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('project-id');
        navigate({ search: newSearchParams.toString() }, { replace: true });
      } else {
        // Project not found
        showNotification({
          title: 'Project Not Found',
          message: `Could not find project with ID: ${projectId}`,
          type: 'error'
        });
        
        // Remove invalid project-id from URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('project-id');
        navigate({ search: newSearchParams.toString() }, { replace: true });
      }
    }
  }, [projects, searchParams, shareModalOpen]);

  // Handle project share - Open modal with selected video URL
  const handleShare = (project: ProjectWithVideos, videoUrl: string | null) => {
    console.log('🎬 Opening share modal with video URL:', videoUrl);
    setSelectedProject(project);
    setSelectedVideoUrl(videoUrl);
    setShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setSelectedProject(null);
    setSelectedVideoUrl(null);
  };

  return (
    <div className={styles.container}>
      <SEO
        title={seoConfig.socialMedia.title}
        description={seoConfig.socialMedia.description}
        keywords={seoConfig.socialMedia.keywords}
        url="https://kingdomstudio.pro/social-media"
      />
      <Navigation />
      
      <main className={styles.main}>
        {/* Header Section */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            <FontAwesomeIcon icon={faShare} className={styles.titleIcon} />
            {t('header.title')}
          </h1>
          <p className={styles.subtitle}>
            {t('header.subtitle')}
          </p>
          
          {/* Action Buttons */}
          <div className={styles.headerActions}>
            <Button
              className={styles.createButton}
              onClick={() => navigate('/create-content')}
              leftSection={<FontAwesomeIcon icon={faPlus} />}
              size="lg"
            >
              {t('actions.createContent')}
            </Button>
          </div>
        </div>

        {/* Connected Accounts Section - Hidden for now */}
        {/* <ConnectedAccountsSection /> */}

        {/* Projects Section with publish state pills */}
        <ProjectGridSection
          projects={projects}
          isLoading={isProjectsLoading}
          error={error}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onShare={handleShare}
          publishedPosts={publishedPosts}
        />
        
      </main>

      {/* Share Modal */}
      {selectedProject && (
        <SocialMediaShareModal
          isOpen={shareModalOpen}
          onClose={handleCloseShareModal}
          project={selectedProject}
          selectedVideoUrl={selectedVideoUrl}
        />
      )}
    </div>
  );
};

export default SocialMediaLanding;
